import {
  CARRIER_DASHBOARD_LANES,
  type CarrierDashboardLane,
  CUSTOMER_LANES,
  type CustomerLane,
} from '@stopal/shared/dashboard-lane'

import type { Tone } from './types'

// Single-source taxonomy: the lane value set lives in @stopal/shared (the API's
// `deriveLane` and its output enum read the same array). Only the TONE is
// declared here, next to `palletStatusPresets`, because tone is this app's
// rendering vocabulary — the shared leaf package has no business knowing that
// design systems exist.
export {
  CUSTOMER_LANES,
  type CustomerLane,
  customerLane,
  type DashboardLane,
  LANE_FLOW,
} from '@stopal/shared/dashboard-lane'

/**
 * What each journey lane MEANS, in tone. Since the lane — not the pallet
 * status — is what every customer surface names, this is the registry those
 * surfaces read; `palletStatusPresets` stays the ops-side equivalent.
 *
 * Exhaustive by construction: `Record<CustomerLane, Tone>` means a lane added
 * to the shared taxonomy fails the build here instead of silently rendering as
 * whatever a default happened to be.
 *
 * The groupings, and why:
 * - `success` — AT REST: nothing is moving, nothing is scheduled, and the box
 *   is where it should be. With us (`stored`), at its new home (`moved`), the
 *   customer's own (`owned`), or theirs under an open rental (`rented`).
 *
 *   The criterion is the JOURNEY, not the money, and it is worth stating
 *   because the obvious misreading is expensive: this is not "nothing is owed"
 *   — `stored` bills warehouse storage the whole time it sits here. Two of
 *   these four lanes are billed and two are not, so a reader looking for "is
 *   this costing me anything" must not reach for the tone. That question is
 *   answered on the pallet's own page, and by the `flows` facet on the
 *   business workspace.
 * - `warning` — physically in motion: on a truck, or being collected. The one
 *   thing a customer scanning a list wants picked out of the settled rows.
 * - `info` — booked, waiting for a truck. Deliberately NOT `warning`: a box
 *   resting at the customer's place with a pickup on the calendar is not a
 *   thing in flight, and colouring every un-settled lane the same amber would
 *   leave most of a real list one colour, which says nothing.
 * - `critical` — the journey stopped without finishing (`cancelled`).
 * - `muted` — over, nothing to do (`closed`).
 *
 * **`rented` is `success`, and this registry contains no `accent`.**
 *
 * The argument for its own tone is a BILLING one — you are still being charged
 * — which is real and is not a journey state, and this is a journey chip. On
 * the journey axis `rented` is simply at rest: the box is at the customer's
 * place, nothing is moving, nothing is scheduled. `owned` and `rented` are the
 * same box in the same place, one bought and one rented; the label already
 * reads "Renting" and the word carries the billing part.
 *
 * `accent` renders as `--brand-primary` (`STRIDE_TONE_CLASSES`), so it does
 * not have a colour of its own — it has whatever colour the BRAND has, and it
 * therefore collides with whichever status tone shares that brand's hue
 * family:
 *
 * - **business** — `[data-brand="business"]` repoints `--status-info` to the
 *   same literal as `--brand-primary`, so `accent` and `info` are identical.
 * - **consumer** — `--status-warning` is `32 95% 44%` and `--brand-primary` is
 *   `32 100% 50%`. The SAME hue, differing only in lightness, so a `rented`
 *   pill and an `in transit` pill would be the same orange as 10% tints.
 *
 * So no brand value fixes it and no repaint moves it somewhere safe — a status
 * scale must not contain the brand colour. `lane-tone-collision.test.ts` pins
 * that: it reads the real token values and fails if any two tones this
 * registry uses land in the same hue family on any surface.
 */
export const palletLaneTones: Record<RenderableLane, Tone> = {
  ed_outbound: 'info',
  fmd_pending: 'info',
  fsp_pending: 'info',
  fsr_outbound: 'info',
  fmd_transit: 'warning',
  fsp_transit: 'warning',
  er_close: 'warning',
  moved: 'success',
  back_home: 'success',
  stored: 'success',
  rented: 'success',
  owned: 'success',
  closed: 'muted',
  cancelled: 'critical',
  // The carrier lanes are OPS-only (`CUSTOMER_LANE_OF` folds them before any
  // customer surface sees one), but `/ops/orders/[id]` renders a raw
  // `DashboardLane` through `laneTone`. Keying this map on `CustomerLane`
  // alone silently dropped them to the `neutral` fallback the moment the two
  // taxonomies were split — colouring `cm_attention`, the one state that means
  // "a person must ring the carrier", in the least alarming tone available.
  cm_booked: 'info',
  cm_transit: 'warning',
  cm_attention: 'critical',
  cer_booked: 'info',
  cer_transit: 'warning',
  cer_attention: 'critical',
}

/**
 * A lane resolved for display: the words the customer reads and the tone that
 * colours them. They travel together because they answer the same question —
 * a surface handed only the label would have to invent a colour.
 */
export interface LaneChip {
  label: string
  tone: Tone
}

/**
 * Every lane a surface can hand this module: the customer set plus the ops-only
 * carrier lanes, which reach `/ops/orders/[id]` unfolded. Exhaustive over both,
 * so a lane added to EITHER taxonomy fails the build here rather than falling
 * through to `neutral`.
 */
type RenderableLane = CustomerLane | CarrierDashboardLane

const RENDERABLE_LANE_SET: ReadonlySet<string> = new Set<string>([
  ...CUSTOMER_LANES,
  ...CARRIER_DASHBOARD_LANES,
])

function isRenderableLane(lane: string): lane is RenderableLane {
  return RENDERABLE_LANE_SET.has(lane)
}

/**
 * The tone for a lane, tolerant of the raw string a payload carries. An
 * unknown lane reads `neutral` rather than throwing — a server that learns a
 * lane before this client is redeployed still renders a legible pill.
 */
export function laneTone(lane: string | null | undefined): Tone {
  if (!lane || !isRenderableLane(lane)) return 'neutral'
  return palletLaneTones[lane]
}
